<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Picker from '../ui/Picker.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { fmt, withCategory, withPaymentType } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { t } from '../../stores/i18n'
import { canSuggest, suggestionsFor, wantSuggestions } from '../../stores/suggest'
import {
  addTransaction,
  deleteTransaction,
  transactionById,
  updateTransaction,
} from '../../stores/transactions'
import { addTag, setTransactionTags, tagIdsOf } from '../../stores/tags'
import { currencySymbol, parseAmount } from '../../lib/money'
import AttachmentsField from '../AttachmentsField.vue'
import { canSee } from '../../stores/attachments'
import { flushDatabase } from '../../db'
import { today as todayISO } from '../../lib/dates'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

// A new row gets suggestions as it is typed: start learning now.
wantSuggestions()

// The row as the list read it — or, opened from elsewhere by id alone, as the
// database has it.
const existing = ref(props.payload?.tx ?? null)

const KINDS = [
  { value: 'expense', key: 'txSheet.spent' },
  { value: 'income', key: 'txSheet.received' },
]

const form = reactive({
  kind: 'expense',
  amount: '',
  description: '',
  date: todayISO(),
  category: '',
  payment_type: '',
  notes: '',
})
const error = ref('')
const confirmDelete = ref(false)
const amountEl = ref(null)
const chosenTags = ref([])
// Files: on a ledger whose files can be seen here (stores/attachments.js), applied on save.
const attachable = canSee()
const filesField = ref(null)

onMounted(async () => {
  if (props.payload?.id && !existing.value) existing.value = await transactionById(props.payload.id)
  const t = existing.value
  if (t) {
    Object.assign(form, {
      kind: t.amount > 0 ? 'income' : 'expense',
      amount: String(Math.abs(t.amount)),
      description: t.description,
      date: t.date,
      category: t.category,
      payment_type: t.payment_type,
      notes: t.notes,
    })
  } else if (props.payload?.prefill) {
    const p = props.payload.prefill
    Object.assign(form, { ...p, amount: p.amount ? String(p.amount) : '' })
  }
  chosenTags.value = existing.value ? [...tagIdsOf(existing.value)] : []
  requestAnimationFrame(() => amountEl.value?.focus())
})

const toggleTag = (id) => {
  chosenTags.value = chosenTags.value.includes(id)
    ? chosenTags.value.filter((t) => t !== id)
    : [...chosenTags.value, id]
}

/** One transaction either wears a tag or it does not. */
const tagLook = (id) => (chosenTags.value.includes(id) ? { tone: 'on' } : { tone: 'off' })

/** A transaction has one category, so picking the one it has takes it off. */
const categoryLook = (name) => (form.category === name ? { tone: 'on' } : { tone: 'off' })
const setCategory = (name) => (form.category = form.category === name ? '' : name)

/** Optional, and one at a time — the same shape as the category. */
const paymentTypeLook = (name) => (form.payment_type === name ? { tone: 'on' } : { tone: 'off' })
const setPaymentType = (name) =>
  (form.payment_type = form.payment_type === name ? '' : name)

/** Typing a name that does not exist yet makes it, so tagging never means
 *  leaving this sheet half-filled to go and create one. */
async function addNamedTag(name) {
  const tag = await addTag(name)
  if (tag && !chosenTags.value.includes(tag.id)) chosenTags.value = [...chosenTags.value, tag.id]
}

const symbol = computed(() => currencySymbol(state.settings.currency))

/**
 * Re-guessed as the description is typed, which is the only field that carries
 * much signal. It costs a scoring pass over one row; the training behind it is
 * cached until the ledger changes.
 */
const suggestion = computed(() =>
  canSuggest.value && !existing.value
    ? suggestionsFor({
        description: form.description,
        // The sign is what tells a refund from a purchase, and the kind
        // toggle is where it lives until the form is saved.
        amount: (form.kind === 'income' ? 1 : -1) * Math.abs(parseAmount(form.amount) || 0),
        date: form.date,
      })
    : { category: null, tags: [] },
)
const percent = (confidence) => `${Math.round(confidence * 100)}%`
const tagName = (id) => state.tags.find((tag) => tag.id === id)?.name ?? ''
const allApplied = computed(
  () =>
    (!suggestion.value.category || form.category === suggestion.value.category.name) &&
    suggestion.value.tags.every((tag) => chosenTags.value.includes(tag.id)),
)
function applyAll() {
  if (suggestion.value.category) form.category = suggestion.value.category.name
  for (const tag of suggestion.value.tags) {
    if (!chosenTags.value.includes(tag.id)) chosenTags.value.push(tag.id)
  }
}

async function save() {
  const amount = parseAmount(form.amount)
  if (!Number.isFinite(amount) || amount === 0) {
    error.value = t('txSheet.needAmount')
    return
  }
  const payload = { ...form, amount }
  const id = existing.value
    ? (await updateTransaction(existing.value.id, payload), existing.value.id)
    : (count('transaction.add', 'hand'), (await addTransaction(payload)).id)

  await setTransactionTags(id, chosenTags.value)
  await filesField.value?.commit(id)
  // On a ledger kept whole in memory (encrypted here, or the IndexedDB fallback),
  // written now rather than after the debounce; on disk this does nothing.
  await flushDatabase().catch(() => {})

  toast(existing.value ? t('txSheet.updated') : t('txSheet.added'), 'good')
  closeSheet()
}

async function destroy() {
  if (!confirmDelete.value) {
    confirmDelete.value = true
    return
  }
  await deleteTransaction(existing.value.id)
  toast(t('txSheet.deleted'))
  closeSheet()
}
</script>

<template>
  <Sheet
    :title="t(existing ? 'txSheet.edit' : 'txSheet.new')"
    :subtitle="t('txSheet.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <div class="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
        <button
          v-for="k in KINDS"
          :key="k.value"
          class="rounded-lg py-2 text-[13.5px] font-semibold transition"
          :class="form.kind === k.value ? 'bg-surface text-text shadow-sm' : 'text-muted'"
          @click="form.kind = k.value"
        >
          {{ t(k.key) }}
        </button>
      </div>

      <div>
        <label class="label" for="tx-amount">{{ t('common.amount') }}</label>
        <div class="relative">
          <span class="absolute top-1/2 left-3.5 -translate-y-1/2 text-lg text-faint">{{ symbol }}</span>
          <input
            id="tx-amount"
            ref="amountEl"
            v-model="form.amount"
            class="input tnum py-3.5 pl-10 text-2xl font-bold"
            inputmode="decimal"
            placeholder="0.00"
            @keydown.enter.prevent="save"
          />
        </div>
      </div>

      <div>
        <label class="label" for="tx-desc">{{ t('txSheet.description') }}</label>
        <input
          id="tx-desc"
          v-model="form.description"
          class="input"
          :placeholder="t('txSheet.descriptionPlaceholder')"
        />
      </div>

      <div>
        <label class="label" for="tx-date">{{ t('txSheet.date') }}</label>
        <input id="tx-date" v-model="form.date" type="date" class="input" />
      </div>

      <div>
        <label class="label" for="tx-cat">{{ t('common.category') }}</label>
        <Picker
          id="tx-cat"
          single
          :create-label="t('common.use')"
          :placeholder="t('common.searchCategories')"
          :value="form.category"
          :options="withCategory(form.category)"
          :look="categoryLook"
          @pick="setCategory"
          @create="setCategory"
        />
      </div>

      <div>
        <label class="label" for="tx-tag">{{ t('charts.tags') }}</label>
        <Picker
          id="tx-tag"
          :options="state.tags"
          :look="tagLook"
          @pick="toggleTag"
          @create="addNamedTag"
          :placeholder="t('common.searchTags')"
        />
      </div>

      <div>
        <label class="label" for="tx-payment">{{ t('txSheet.paymentType') }}</label>
        <Picker
          id="tx-payment"
          single
          :create-label="t('common.use')"
          :placeholder="t('txSheet.paymentTypePlaceholder')"
          :value="form.payment_type"
          :options="withPaymentType(form.payment_type)"
          :look="paymentTypeLook"
          @pick="setPaymentType"
          @create="setPaymentType"
        />
      </div>

      <div>
        <label class="label" for="tx-notes">{{ t('txSheet.notes') }}</label>
        <textarea id="tx-notes" v-model="form.notes" class="input min-h-[68px]" rows="2" />
      </div>

      <AttachmentsField
        v-if="attachable"
        ref="filesField"
        :key="existing?.id ?? 'new'"
        :transaction-id="existing?.id ?? null"
      />

      <!-- What the ledger thinks this is. Nothing is applied until it is
           tapped: a guess that fills the form in behind you is worse than no
           guess at all. -->
      <div
        v-if="suggestion.category || suggestion.tags.length"
        class="rounded-xl border border-line-soft bg-raised/50 p-3.5"
      >
        <p class="flex items-center gap-2 text-[12.5px] font-semibold text-muted">
          <Icon name="spark" :size="15" /> {{ t('suggest.title') }}
        </p>
        <div class="mt-2 flex flex-wrap items-center gap-1.5">
          <button
            v-if="suggestion.category"
            class="chip !py-1 !text-[12.5px]"
            :class="{ 'chip-on': form.category === suggestion.category.name }"
            @click="setCategory(suggestion.category.name)"
          >
            <Icon name="folder" :size="12" />
            {{ suggestion.category.name }}
            <span class="text-faint">{{ percent(suggestion.category.confidence) }}</span>
          </button>
          <button
            v-for="tag in suggestion.tags"
            :key="tag.id"
            class="chip !py-1 !text-[12.5px]"
            :class="{ 'chip-on': chosenTags.includes(tag.id) }"
            @click="toggleTag(tag.id)"
          >
            <Icon name="tag" :size="12" />
            {{ tagName(tag.id) }}
            <span class="text-faint">{{ percent(tag.confidence) }}</span>
          </button>
        </div>
        <button
          v-if="!allApplied"
          class="btn-ghost mt-1.5 !px-1 !text-[12.5px] text-accent"
          @click="applyAll"
        >
          {{ t('suggest.applyAll') }}
        </button>
      </div>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>
    </div>

    <template #footer>
      <button v-if="existing" class="btn-danger" @click="destroy">
        <Icon name="trash" :size="16" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <div class="flex-1" />
      <button class="btn-soft" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="save">
        <Icon name="check" :size="16" /> {{ t('common.save') }}
        <Kbd combo="mod+enter" />
      </button>
    </template>
  </Sheet>
</template>
